import { useState } from "react";
import api, { apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Users, Plus, X, Save } from "lucide-react";
import { POSITION_SLOTS, getStartingLineup, pitchScreenX, pitchScreenY } from "../lib/engine";
import Jersey, { JERSEY_STYLES, defaultJersey, gkColors } from "./Jersey";

const JSLOTS = [["body", "Body"], ["sleeves", "Sleeves"], ["accent", "Pattern"], ["trim", "Collar"]];
const uid = () => Math.random().toString(36).slice(2, 9);
const withIds = (sq) => (sq || []).map((p) => ({ ...p, _id: p._id || uid() }));

// Build a 15-long array of player ids for the starting positions from stored numbers.
function initXvIds(startingXV, squad) {
  return getStartingLineup({ startingXV, squad }).map((p) => p?._id || null);
}

export default function SquadManager({ team, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(team.name);
  const [kit, setKit] = useState(() => ({ ...defaultJersey(team.colorPrimary), ...(team.jersey || {}) }));
  const [logo] = useState(team.logo || "shield");
  const [gender, setGender] = useState(team.gender || "boys");
  const [squad, setSquad] = useState(() => withIds(team.squad));
  const [xvIds, setXvIds] = useState(() => initXvIds(team.startingXV, squad));
  const [activeSlot, setActiveSlot] = useState(0);
  const nextEmpty = (from) => { for (let k = 1; k <= 15; k++) { const idx = (from + k) % 15; if (!xvIds[idx]) return idx; } return (from + 1) % 15; };
  const [saving, setSaving] = useState(false);
  const duplicateStarters = xvIds.filter(Boolean).length !== new Set(xvIds.filter(Boolean)).size;

  const reset = () => {
    const sq = withIds(team.squad);
    setName(team.name);
    setKit({ ...defaultJersey(team.colorPrimary), ...(team.jersey || {}) });
    setGender(team.gender || "boys");
    setSquad(sq);
    setXvIds(initXvIds(team.startingXV, sq));
  };

  const setRow = (id, patch) => setSquad((s) => s.map((p) => (p._id === id ? { ...p, ...patch } : p)));
  const addRow = () => setSquad((s) => [...s, { _id: uid(), number: (Math.max(0, ...s.map((p) => p.number || 0)) || 0) + 1, name: "" }]);
  const delRow = (id) => { setSquad((s) => s.filter((p) => p._id !== id)); setXvIds((a) => a.map((v) => (v === id ? null : v))); };

  // assign a player to a position; remove that player from any other slot (no duplicates)
  const setSlot = (i, id) => setXvIds((a) => a.map((v, j) => (j === i ? (id || null) : (id && v === id ? null : v))));

  const panel = squad.map((p, index) => ({ ...p, index, slotIndex: xvIds.indexOf(p._id) }))
    .sort((a, b) => (a.slotIndex < 0 ? 15 + a.index : a.slotIndex) - (b.slotIndex < 0 ? 15 + b.index : b.slotIndex));

  const save = async () => {
    if (duplicateStarters) { toast.error("A player is selected twice. Choose a different player in the repeated Starting 15 positions."); return; }
    setSaving(true);
    try {
      const idToNum = {}; squad.forEach((p) => { idToNum[p._id] = p.number; });
      const startingXV = xvIds.map((id) => (id ? idToNum[id] : null));
      const cleanSquad = squad.map(({ _id, ...p }) => p); // eslint-disable-line no-unused-vars
      const { data } = await api.put(`/teams/${team.id}`, { name, gender, colorPrimary: kit.body, colorSecondary: kit.trim, logo, squad: cleanSquad, startingXV, jersey: kit });
      toast.success("Team saved");
      onSaved?.(data);
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="squad-manager-btn" variant="secondary"><Users className="h-4 w-4 mr-1" />Squad</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Team &amp; Squad</DialogTitle>
          <DialogDescription>Enter your panel once, then pick your Starting 15 and their positions. This is shared with every coach on the team.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {duplicateStarters && <p role="alert" data-testid="duplicate-starters-warning" className="text-xs text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-md p-3">Your saved lineup has the same player in more than one position. Select a different player in the repeated positions before saving.</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground">Team name</label>
              <Input data-testid="squad-team-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Team</label>
              <div className="mt-1 flex rounded-md border border-border overflow-hidden h-9 w-max">
                {[["boys", "Boys"], ["girls", "Girls"]].map(([g, label]) => (
                  <button key={g} data-testid={`squad-gender-${g}`} onClick={() => setGender(g)} className={`px-4 text-xs font-semibold ${gender === g ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{label}</button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] text-muted-foreground">Jersey</label>
            <div className="mt-1 flex gap-4 items-start rounded-lg border border-border p-3 bg-secondary/30">
              <div className="rounded-lg bg-gradient-to-b from-[#0d3d22] to-[#0a2e1a] p-2 border border-border shrink-0">
                <Jersey colors={kit} number={10} size={92} />
              </div>
              <div className="flex-1 space-y-2.5 min-w-0">
                <div className="flex flex-wrap gap-3">
                  {JSLOTS.map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-xs">
                      <input data-testid={`jersey-color-${k}`} type="color" value={kit[k] || "#ffffff"} onChange={(e) => setKit((p) => ({ ...p, [k]: e.target.value }))} className="h-7 w-7 rounded border border-border bg-transparent cursor-pointer p-0" />
                      <span className="text-muted-foreground">{l}</span>
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                  {JERSEY_STYLES.map((st) => (
                    <button key={st.id} data-testid={`jersey-style-${st.id}`} title={st.name} onClick={() => setKit((p) => ({ ...p, style: st.id }))}
                      className={`rounded-md p-1 border transition-colors ${kit.style === st.id ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border hover:border-muted-foreground/50"}`}>
                      <Jersey colors={{ ...kit, style: st.id }} size={36} showNumber={false} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Panel */}
            <div className="flex min-h-0 flex-col">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Panel · {squad.length}</label>
                <Button size="sm" variant="ghost" onClick={addRow} data-testid="add-player-row"><Plus className="h-4 w-4 mr-1" />Add player</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2" data-testid="squad-numbering-help">Numbers and positions follow your Starting 15. Pick a pitch position to move a player.</p>
              <div className="relative min-h-0 md:flex-1">
              <div data-testid="squad-panel-list" className="space-y-1.5 max-h-[42vh] overflow-y-auto pr-1 md:absolute md:inset-0 md:max-h-none">
                {panel.map((p) => (
                  <div key={p._id} className="flex items-center gap-2" data-testid={`squad-row-${p.index}`}>
                    <span data-testid={`squad-number-${p.index}`} className="h-9 w-10 shrink-0 rounded-md border border-border bg-secondary/40 flex items-center justify-center font-mono2 text-sm font-bold text-primary" aria-label={p.slotIndex >= 0 ? `Jersey number ${p.slotIndex + 1}` : "No starting jersey"}>
                      {p.slotIndex >= 0 ? `#${p.slotIndex + 1}` : "—"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <Input data-testid={`squad-name-${p.index}`} value={p.name} onChange={(e) => setRow(p._id, { name: e.target.value })} placeholder="Player name" aria-label="Player name" className="h-8" />
                      <div data-testid={`squad-position-${p.index}`} className="text-[10px] text-muted-foreground mt-0.5">{p.slotIndex >= 0 ? POSITION_SLOTS[p.slotIndex].label : "Substitute · not in Starting 15"}</div>
                    </div>
                    <button onClick={() => delRow(p._id)} data-testid={`squad-del-${p.index}`} aria-label={`Remove ${p.name || "player"}`} className="text-muted-foreground hover:text-destructive shrink-0"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {!squad.length && <div className="text-xs text-muted-foreground py-4 text-center">Add your panel of players above.</div>}
              </div>
              </div>
            </div>

            {/* Starting 15 — visual lineup */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Starting 15 · tap a position</label>
              <div className="relative w-full rounded-lg border border-border overflow-hidden" style={{ aspectRatio: "90 / 145", background: "linear-gradient(#0e4a26,#0a3419)" }} data-testid="xv-pitch">
                <div className="absolute left-0 right-0 top-1/2 border-t border-white/15" />
                {POSITION_SLOTS.map((slot, i) => {
                  const pid = xvIds[i];
                  const pl = pid ? squad.find((p) => p._id === pid) : null;
                  const cols = i === 0 ? gkColors("A") : kit;
                  const active = activeSlot === i;
                  return (
                    <button key={i} type="button" data-testid={`xv-slot-${i}`} onClick={() => setActiveSlot(i)}
                      aria-label={`#${i + 1} ${slot.label}: ${pl?.name || "Empty"}`}
                      className="absolute flex flex-col items-center" style={{ left: `${(pitchScreenX(slot.x) / 90) * 100}%`, top: `${(pitchScreenY(slot.y) / 145) * 100}%`, transform: "translate(-50%,-50%)" }}>
                      <div className={`rounded-full transition-shadow ${active ? "ring-2 ring-[#00F0FF] ring-offset-1 ring-offset-[#0a3419]" : ""}`}>
                        <Jersey colors={cols} number={i + 1} size={30} />
                      </div>
                      <span className={`mt-0.5 text-[8px] leading-none rounded px-1 py-0.5 max-w-[58px] truncate ${pl ? "bg-black/55 text-white/95" : "bg-black/35 text-white/45"}`}>{pl ? pl.name : slot.short}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-2">
                <div data-testid="xv-active-position" className="text-[11px] text-muted-foreground mb-1">{activeSlot + 1}. {POSITION_SLOTS[activeSlot].label} — pick a player (wears #{activeSlot + 1})</div>
                <div className="flex flex-wrap gap-1 max-h-[15vh] overflow-y-auto pr-1">
                  <button type="button" data-testid="xv-pick-empty" onClick={() => setSlot(activeSlot, "")} className="text-[11px] rounded-full border border-border px-2 py-1 text-muted-foreground hover:border-destructive/60">— empty —</button>
                  {squad.map((p) => {
                    const here = xvIds[activeSlot] === p._id;
                    const usedElsewhere = xvIds.some((id, idx) => id === p._id && idx !== activeSlot);
                    return (
                      <button key={p._id} type="button" data-testid={`xv-pick-${p._id}`} onClick={() => { setSlot(activeSlot, p._id); setActiveSlot((s) => nextEmpty(s)); }}
                        className={`text-[11px] rounded-full border px-2 py-1 transition-colors ${here ? "bg-primary text-primary-foreground border-primary" : usedElsewhere ? "opacity-45 border-border" : "border-border hover:border-primary/50"}`}>
                        {p.name || "(no name)"}{usedElsewhere ? " ·moves" : ""}
                      </button>
                    );
                  })}
                  {!squad.length && <span className="text-xs text-muted-foreground py-2">Add players in the panel first.</span>}
                </div>
              </div>
            </div>
          </div>

          <Button data-testid="squad-save" onClick={save} disabled={saving} className="w-full"><Save className="h-4 w-4 mr-1" />{saving ? "Saving…" : "Save team"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
